import React, { useEffect, useState } from 'react';
import { useUserSettings } from '../contexts/UserSettingsContext';
import { useUser } from '../contexts/UserContext';
import { useAchievements } from '../contexts/AchievementContext';
import { useNavigate } from 'react-router-dom';
import apiService from '../services/api';
import ModalPortal from '../components/ModalPortal';
import '../styles/Settings.css';

const Settings = ({ sidebarOpen }) => {
  const { settings, updateSetting, resetSettings } = useUserSettings();
  const { user } = useUser();
  const { isShowingAchievement } = useAchievements();
  const navigate = useNavigate();
  const [showSuccess, setShowSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);

  const showSuccessMessage = (message) => {
    // If an achievement is being shown, delay the success message
    if (isShowingAchievement) {
      setTimeout(() => {
        showSuccessMessage(message);
      }, 6000); // Wait 6 seconds (5 for achievement + 1 buffer)
      return;
    }
    
    setSuccessMessage(message);
    setShowSuccess(true);
    setTimeout(() => setShowSuccess(false), 3000);
  };

  const hasRecipeProfile = Boolean(user?.favoriteCuisine && user?.cookingExperience);

  useEffect(() => {
    if (settings.prioritizeRecipes && user && !hasRecipeProfile) {
      updateSetting('prioritizeRecipes', false);
    }
  }, [hasRecipeProfile, settings.prioritizeRecipes, updateSetting, user]);

  const handleSettingChange = (key, value) => {
    if (key === 'prioritizeRecipes' && value && !hasRecipeProfile) {
      showSuccessMessage('Add your favorite cuisine and cooking experience in Profile before turning this on.');
      navigate('/profile');
      return;
    }

    updateSetting(key, value);
    showSuccessMessage('Setting updated successfully!');
  };

  const handleResetSettings = () => {
    if (window.confirm('Are you sure you want to reset all settings to default values?')) {
      resetSettings();
      showSuccessMessage('Settings reset to default values!');
    }
  };

  const handleReplayTutorial = () => {
    window.dispatchEvent(new Event('startOnboardingTour'));
    showSuccessMessage('Tutorial restarted. Follow the steps or skip anytime.');
  };


  const handleDeleteProfile = async () => {
    if (deleteConfirmation !== 'DELETE') {
      showSuccessMessage('Please type "DELETE" to confirm profile deletion');
      return;
    }

    setIsDeleting(true);
    try {
      const response = await apiService.deleteProfile();
      console.log('Delete profile response:', response);
      
      // Check if deletion was successful
      if (response.success) {
        // Close modal first
        setShowDeleteModal(false);
        setDeleteConfirmation('');
        
        // Show success message
        showSuccessMessage('Profile deleted successfully. Redirecting...');
        
        // Clear user data and redirect after a short delay
        setTimeout(() => {
          // Clear token and redirect without calling logout API
          apiService.removeToken();
          navigate('/login');
          // Force page reload to clear any cached state
          window.location.reload();
        }, 2000);
      } else {
        throw new Error('Deletion was not successful');
      }
      
    } catch (error) {
      console.error('Error deleting profile:', error);
      showSuccessMessage('Failed to delete profile. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className={`settings-container ${sidebarOpen ? 'sidebar-open' : ''}`}>
      <div className="settings-content">
        {/* Header */}
        <div className="settings-header">
          <div>
            <h1 className="settings-title">
              <span>⚙️</span>
              Settings
            </h1>
            <p className="settings-subtitle">Customize your app experience</p>
          </div>
        </div>

        {/* Settings Sections */}
        <div className="settings-sections">
        {/* Shopping List & Pantry Integration */}
        <div className="settings-section">
          <div className="section-header">
            <h2 className="section-title">
              <span className="section-icon">🛒</span>
              Shopping List & Pantry Integration
            </h2>
            <p className="section-description">
              Configure how shopping list items interact with your pantry
            </p>
          </div>

          <div className="settings-group">
            <div className="setting-item">
              <div className="setting-info">
                <h3 className="setting-title">Auto-add to Pantry</h3>
                <p className="setting-description">
                  Automatically add completed shopping list items to your pantry
                </p>
              </div>
              <div className="setting-control">
                <label className="toggle-switch">
                  <input
                    type="checkbox"
                    checked={settings.autoAddToPantry}
                    onChange={(e) => handleSettingChange('autoAddToPantry', e.target.checked)}
                  />
                  <span className="toggle-slider"></span>
                </label>
              </div>
            </div>

            <div className="setting-item">
              <div className="setting-info">
                <h3 className="setting-title">Disable Expiry Tracking</h3>
                <p className="setting-description">
                  Hide expiry dates, expiry reminders, and expiry-related alerts in pantry
                </p>
              </div>
              <div className="setting-control">
                <label className="toggle-switch">
                  <input
                    type="checkbox"
                    checked={settings.disableExpiryTracking}
                    onChange={(e) => handleSettingChange('disableExpiryTracking', e.target.checked)}
                  />
                  <span className="toggle-slider"></span>
                </label>
              </div>
            </div>

            {settings.autoAddToPantry && (
              <>
                <div className="setting-item">
                  <div className="setting-info">
                    <h3 className="setting-title">Default Pantry Location</h3>
                    <p className="setting-description">
                      Where to place items when auto-adding to pantry
                    </p>
                  </div>
                  <div className="setting-control">
                    <select
                      value={settings.pantryDefaultLocation}
                      onChange={(e) => handleSettingChange('pantryDefaultLocation', e.target.value)}
                      className="setting-select"
                    >
                      <option value="pantry">🏠 Pantry</option>
                      <option value="fridge">🧊 Fridge</option>
                      <option value="freezer">❄️ Freezer</option>
                      <option value="counter">🏠 Counter</option>
                      <option value="cabinet">🏠 Cabinet</option>
                    </select>
                  </div>
                </div>

                <div className="setting-item">
                  <div className="setting-info">
                    <h3 className="setting-title">Default Expiry Days</h3>
                    <p className="setting-description">
                      How many days from now to set as expiry date for auto-added items
                    </p>
                  </div>
                  <div className="setting-control">
                    {settings.disableExpiryTracking ? (
                      <span className="setting-description">Disabled while expiry tracking is off</span>
                    ) : (
                      <>
                        <input
                          type="number"
                          min="1"
                          max="365"
                          value={settings.pantryDefaultExpiryDays}
                          onChange={(e) => handleSettingChange('pantryDefaultExpiryDays', parseInt(e.target.value) || 7)}
                          className="setting-input"
                        />
                        <span className="input-suffix">days</span>
                      </>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Recipe Preferences */}
        <div className="settings-section">
          <div className="section-header">
            <h2 className="section-title">
              <span className="section-icon">🍽️</span>
              Recipe Preferences
            </h2>
            <p className="section-description">
              Customize how recipes are displayed and prioritized
            </p>
          </div>

          <div className="settings-group">
            <div className="setting-item">
              <div className="setting-info">
                <h3 className="setting-title">Prioritize Recipes Based on Profile</h3>
                <p className="setting-description">
                  Show recipes matching your favorite cuisine and cooking experience first
                </p>
                {!hasRecipeProfile && (
                  <p className="setting-warning">
                    Add favorite cuisine and cooking experience in Profile to use this.
                  </p>
                )}
              </div>
              <div className="setting-control">
                <label className="toggle-switch">
                  <input
                    type="checkbox"
                    checked={settings.prioritizeRecipes}
                    onChange={(e) => handleSettingChange('prioritizeRecipes', e.target.checked)}
                  />
                  <span className="toggle-slider"></span>
                </label>
              </div>
            </div>
          </div>
        </div>

        {/* General Settings */}
        <div className="settings-section">
          <div className="section-header">
            <h2 className="section-title">
              <span className="section-icon">🔧</span>
              General Settings
            </h2>
            <p className="section-description">
              General app preferences and configurations
            </p>
          </div>

          <div className="settings-group">
            <div className="setting-item">
              <div className="setting-info">
                <h3 className="setting-title">Reset All Settings</h3>
                <p className="setting-description">
                  Reset all settings to their default values
                </p>
              </div>
              <div className="setting-control">
                <button
                  onClick={handleResetSettings}
                  className="reset-settings-btn"
                >
                  Reset Settings
                </button>
              </div>
            </div>

            <div className="setting-item">
              <div className="setting-info">
                <h3 className="setting-title">Replay Tutorial</h3>
                <p className="setting-description">
                  Start the onboarding walkthrough again to see what each page does.
                </p>
              </div>
              <div className="setting-control">
                <button
                  onClick={handleReplayTutorial}
                  className="replay-tutorial-btn"
                >
                  Replay Tutorial
                </button>
              </div>
            </div>

            <div className="setting-item">
              <div className="setting-info">
                <h3 className="setting-title">Legal Documents</h3>
                <p className="setting-description">
                  Read the Terms of Service and Privacy Policy any time.
                </p>
              </div>
              <div className="setting-control legal-links-control">
                <button
                  type="button"
                  onClick={() => navigate('/terms')}
                  className="legal-link-btn"
                >
                  Terms
                </button>
                <button
                  type="button"
                  onClick={() => navigate('/privacy')}
                  className="legal-link-btn"
                >
                  Privacy
                </button>
              </div>
            </div>
          </div>
        </div>


        {/* Account Management */}
        <div className="settings-section danger-section">
          <div className="section-header">
            <h2 className="section-title">
              <span className="section-icon">⚠️</span>
              Account Management
            </h2>
            <p className="section-description">
              Manage your account and data
            </p>
          </div>

          <div className="settings-group">
            <div className="setting-item">
              <div className="setting-info">
                <h3 className="setting-title">Delete Profile</h3>
                <p className="setting-description">
                  Permanently delete your account and all associated data. This action cannot be undone.
                  <br />
                  <strong>Note:</strong> Your recipes will be kept but anonymized (author email will be set to default).
                </p>
              </div>
              <div className="setting-control">
                <button
                  onClick={() => setShowDeleteModal(true)}
                  className="delete-profile-btn"
                >
                  Delete Profile
                </button>
              </div>
            </div>
          </div>
        </div>
        </div>
      </div>

      {/* Delete Profile Confirmation Modal */}
      {showDeleteModal && (
        <ModalPortal>
        <div className="ingredient-modal-overlay" onClick={() => setShowDeleteModal(false)}>
          <div className="ingredient-modal-container" onClick={(e) => e.stopPropagation()}>
            <div className="ingredient-modal-header">
              <h2 className="ingredient-modal-title">DELETE PROFILE</h2>
              <div className="ingredient-modal-subheader">
                <span className="quick-kitchen">ACCOUNT MANAGEMENT</span>
                <span className="modal-context-note">Permanent action</span>
              </div>
              <button className="ingredient-modal-close" onClick={() => setShowDeleteModal(false)}>×</button>
            </div>
            <div className="ingredient-modal-content">
              <div className="professional-warning">
                <div className="warning-header">
                  <h3>⚠️ Permanent Account Deletion</h3>
                  <p className="warning-subtitle">This action cannot be undone</p>
                </div>
                
                <div className="warning-content">
                  <div className="warning-section">
                    <h4>What will be deleted:</h4>
                    <div className="deletion-list">
                      <div className="deletion-item">
                        <span className="deletion-icon">👤</span>
                        <span>Your account and login credentials</span>
                      </div>
                      <div className="deletion-item">
                        <span className="deletion-icon">🏆</span>
                        <span>All your activities and achievements</span>
                      </div>
                      <div className="deletion-item">
                        <span className="deletion-icon">📦</span>
                        <span>Your pantry items and shopping lists</span>
                      </div>
                      <div className="deletion-item">
                        <span className="deletion-icon">📅</span>
                        <span>Your meal plans and custom products</span>
                      </div>
                      <div className="deletion-item">
                        <span className="deletion-icon">🔒</span>
                        <span>All your personal data</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="warning-section">
                    <h4>What will be preserved:</h4>
                    <div className="preservation-note">
                      <span className="preservation-icon">🍳</span>
                      <span>Your recipes will be kept but anonymized (author email will be set to "anonymous@cheftracker.com")</span>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="confirmation-input">
                <label htmlFor="delete-confirmation">
                  To confirm deletion, type <strong>DELETE</strong> in the box below:
                </label>
                <input
                  id="delete-confirmation"
                  type="text"
                  value={deleteConfirmation}
                  onChange={(e) => setDeleteConfirmation(e.target.value)}
                  placeholder="Type DELETE to confirm"
                  className="confirmation-text-input"
                />
              </div>
            </div>
            <div className="ingredient-modal-footer">
              <button 
                className="ingredient-clear-btn"
                onClick={() => {
                  setShowDeleteModal(false);
                  setDeleteConfirmation('');
                }}
                disabled={isDeleting}
              >
                Cancel
              </button>
              <button 
                className="ingredient-apply-btn"
                onClick={handleDeleteProfile}
                disabled={isDeleting || deleteConfirmation !== 'DELETE'}
                style={{backgroundColor: '#dc2626', borderColor: '#dc2626'}}
              >
                {isDeleting ? 'Deleting...' : 'Delete Profile'}
              </button>
            </div>
          </div>
        </div>
        </ModalPortal>
      )}

      {/* Achievement-Style Success Notification */}
      {showSuccess && (
        <div className="success-notification">
          <div className="success-content">
            <span className="success-icon">✅</span>
            <div className="success-text">
              <strong>Success!</strong>
              <span>{successMessage}</span>
            </div>
            <button className="success-close" onClick={() => setShowSuccess(false)}>×</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Settings;
